import type { ReactNode } from 'react';
import { fleeChancePercent } from '../../domain/combat/flee';
import type { DungeonRoom, RoomOption } from '../../domain/game/types';
import { useGameStore } from '../../store/gameStore';
import {
  selectCurrentRoom,
  selectLastMessage,
  selectMode,
  selectPlayer,
} from '../../store/selectors';

function roomEyebrow(room: DungeonRoom): string {
  switch (room.type) {
    case 'SAFE':
      return 'KHU VỰC AN TOÀN';
    case 'NPC':
      return 'GẶP GỠ';
    case 'NORMAL_ENEMY':
    case 'ELITE_ENEMY':
    case 'BOSS':
      return 'CHẠM TRÁN';
    case 'TREASURE':
      return 'BÁU VẬT';
    case 'TRAP':
      return 'BẪY';
    case 'SECRET':
      return 'BÍ MẬT';
    case 'TELEPORTER':
      return 'DỊCH CHUYỂN';
    default:
      return 'PHÒNG HIỆN TẠI';
  }
}

function visibleOptions(room: DungeonRoom) {
  if (room.isRequiredInteraction && room.isCleared) {
    return room.options.filter(
      (opt) => !opt.consequence.setCleared && !opt.consequence.triggerCombat && !opt.consequence.attemptFlee,
    );
  }
  return room.options;
}

function optionLabel(option: RoomOption, fleePercent: number): string {
  if (option.consequence.attemptFlee || option.action === 'ESCAPE') {
    return `🏃 Bỏ qua / chạy trốn (~${fleePercent}% · DEX)`;
  }
  return option.actionText;
}

function optionTone(option: RoomOption): 'fight' | 'flee' | 'gold' | 'default' {
  if (option.consequence.triggerCombat) {
    return 'fight';
  }
  if (option.consequence.attemptFlee || option.action === 'ESCAPE') {
    return 'flee';
  }
  if (option.consequence.gainGold) {
    return 'gold';
  }
  return 'default';
}

export function ContextPanel() {
  const mode = useGameStore(selectMode);
  const room = useGameStore(selectCurrentRoom);
  const player = useGameStore(selectPlayer);
  const lastMessage = useGameStore(selectLastMessage);
  const selectOption = useGameStore((state) => state.selectOption);
  const reset = useGameStore((state) => state.reset);
  const fleePercent = fleeChancePercent(player.stats);

  if (mode.type === 'dead' || !player.alive) {
    return (
      <ContextCard eyebrow="KẾT THÚC" title="Bạn đã ngã xuống">
        <p className="context-body">
          Với chỉ 1 sinh mệnh, mọi đòn trúng đều là dấu chấm hết.
        </p>
        <button className="primary-button" type="button" onClick={reset}>
          Chơi lại từ đầu
        </button>
      </ContextCard>
    );
  }

  if (!room) {
    return (
      <ContextCard eyebrow="LỖI" title="Không có phòng">
        <p className="context-body">Dữ liệu tầng bị thiếu phòng hiện tại.</p>
      </ContextCard>
    );
  }

  const options = visibleOptions(room);

  return (
    <ContextCard eyebrow={roomEyebrow(room)} title={room.name}>
      <p className="room-safety">{room.safetyTag}</p>
      <p className="context-body">{room.description}</p>

      {lastMessage ? (
        <p className="context-toast" role="status">
          {lastMessage}
        </p>
      ) : null}

      {room.isCleared && room.isRequiredInteraction ? (
        <div className="status-chip">Đã xử lý — có thể di chuyển tiếp</div>
      ) : null}

      {options.length > 0 ? (
        <div className="option-list">
          {options.map((option) => {
            const tone = optionTone(option);
            return (
              <button
                key={option.id}
                className={`choice-button tone-${tone}`}
                type="button"
                onClick={() => selectOption(option.id)}
              >
                {optionLabel(option, fleePercent)}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="status-chip">Di chuyển bằng bản đồ hoặc WASD</div>
      )}
    </ContextCard>
  );
}

interface ContextCardProps {
  eyebrow: string;
  title: string;
  children: ReactNode;
}

function ContextCard({ eyebrow, title, children }: ContextCardProps) {
  return (
    <section className="context-card">
      <small>{eyebrow}</small>
      <h2>{title}</h2>
      {children}
    </section>
  );
}
